import { useEffect, useState } from 'react';
import EmojiPicker, { EmojiClickData, EmojiStyle } from 'emoji-picker-react';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { CampaignDateHeatmapPicker } from '../ui/CampaignDateHeatmapPicker';
import { CAMPAIGN_TYPES } from '../../lib/enums';
import { CampaignType } from '../../lib/types';
import BrandChipPicker from '../ui/BrandChipPicker';
import { useCampaigns, useCreateCampaign, useUpdateCampaign } from '../../lib/api/campaigns';
import { useBrands, slugify } from '../../lib/api/brands';
import { useToast } from '../ui/Toast';
import { Campaign } from '../../lib/types';
import AiTextSuggest from '../partnerships/AiTextSuggest';
import axios from '../../utils/axiosInstance';
import BrandRemovalCheck, { RemovalItem } from './BrandRemovalCheck';
import { listAssets, useUpdateAsset } from '../../lib/api/assets';
import { assetDisplayTitle } from '../../lib/assetTitle';
import { fmtShort } from '../../lib/dates';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultBrandId?: string;
  onCreated?: (id: string) => void;
  /** When set, modal switches to edit mode: PATCHes this campaign instead of creating. */
  campaign?: Campaign | null;
}

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDaysISO = (iso: string, days: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

export const CreateCampaignModal = ({ open, onClose, defaultBrandId, onCreated, campaign }: Props) => {
  const toast = useToast();
  const { data: brands = [] } = useBrands();
  const { data: allCampaigns = [] } = useCampaigns();
  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign();
  const updateAsset = useUpdateAsset();
  const editing = !!campaign;
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [type, setType] = useState<CampaignType>('Brand');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(addDaysISO(todayISO(), 14));
  const [selectedBrands, setSelectedBrands] = useState<string[]>(defaultBrandId ? [defaultBrandId] : []);
  const [parent, setParent] = useState<string>('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setEmojiOpen(false);
    if (campaign) {
      setName(campaign.name);
      setEmoji(campaign.emoji ?? '');
      setType(campaign.type);
      setStartDate(campaign.start_date ?? '');
      setEndDate(campaign.end_date ?? '');
      setSelectedBrands((campaign.brand_details ?? []).map((b) => b.id));
      setParent(campaign.parent ?? '');
      setDescription(campaign.description ?? '');
    } else {
      setName('');
      setEmoji('');
      setType('Brand');
      setStartDate(todayISO());
      setEndDate(addDaysISO(todayISO(), 14));
      setSelectedBrands(defaultBrandId ? [defaultBrandId] : []);
      setParent('');
      setDescription('');
    }
  }, [open, defaultBrandId, campaign]);

  const computedId = slugify(name) ? `c-${slugify(name)}` : '';
  const id = editing ? campaign!.id : computedId;
  const pending = createCampaign.isPending || updateCampaign.isPending;
  const canSubmit = !!name.trim() && !!id && !!startDate && !!endDate && !pending;

  const toggleBrand = (bid: string) => {
    setSelectedBrands((prev) =>
      prev.includes(bid) ? prev.filter((x) => x !== bid) : [...prev, bid]
    );
  };

  // Brands taken off the campaign: remind which placements they're still on.
  const [removalItems, setRemovalItems] = useState<RemovalItem[] | null>(null);
  const [removalBusy, setRemovalBusy] = useState(false);
  const submit = async () => {
    if (!canSubmit) return;
    const removed = editing ? (campaign!.brands ?? []).filter((b) => !selectedBrands.includes(b)) : [];
    if (!removed.length) return doSubmit([]);
    try {
      const assets = await listAssets({ campaign: campaign!.id });
      const items = removed.flatMap((bid) => {
        const on = assets.filter((a) => ((a.brands as string[]) ?? []).includes(bid));
        if (!on.length) return [];
        return [{
          brandId: bid,
          brandName: brands.find((b) => b.id === bid)?.name ?? bid,
          lines: [`Still on ${on.length} placement${on.length === 1 ? '' : 's'}: ${on.slice(0, 6).map((a) => `${assetDisplayTitle(a)}${a.publish_date ? ` · ${fmtShort(a.publish_date)}` : ''}`).join(', ')}${on.length > 6 ? ` and ${on.length - 6} more` : ''}.`],
          option: `Also remove it from ${on.length === 1 ? 'this placement' : `these ${on.length} placements`}`,
        }];
      });
      if (!items.length) return doSubmit([]);
      setRemovalItems(items);
    } catch {
      doSubmit([]);
    }
  };
  const doSubmit = async (alsoFromPlacements: string[]) => {
    setRemovalItems(null);
    try {
      if (editing && alsoFromPlacements.length) {
        setRemovalBusy(true);
        const assets = await listAssets({ campaign: campaign!.id });
        for (const a of assets) {
          const tagged = (a.brands as string[]) ?? [];
          if (tagged.some((b) => alsoFromPlacements.includes(b))) {
            await updateAsset.mutateAsync({ id: a.id, patch: { brands: tagged.filter((b) => !alsoFromPlacements.includes(b)) } });
          }
        }
        setRemovalBusy(false);
      }
      if (editing) {
        const updated = await updateCampaign.mutateAsync({
          id: campaign!.id,
          patch: {
            name: name.trim(),
            emoji,
            type,
            start_date: startDate || null,
            end_date: endDate || null,
            description,
            brands: selectedBrands,
            parent: parent || null,
          } as Partial<Campaign>,
        });
        toast.push(`Campaign "${updated.name}" updated`);
        onCreated?.(updated.id);
      } else {
        const created = await createCampaign.mutateAsync({
          id,
          name: name.trim(),
          emoji,
          type,
          start_date: startDate || null,
          end_date: endDate || null,
          description,
          brands: selectedBrands,
          parent: parent || null,
        });
        toast.push(`Campaign "${created.name}" created`);
        onCreated?.(created.id);
      }
      onClose();
    } catch (err) {
      setRemovalBusy(false);
      console.error(err);
      toast.push(editing ? 'Could not update campaign' : 'Could not create campaign');
    }
  };

  // Parent candidates: any other campaign (incl. already-ended ones, so
  // Doreen can retro-nest a campaign under last year's annual umbrella).
  // Sorted newest-start first so active + upcoming show at the top.
  const todayStr = todayISO();
  const parentOptions = allCampaigns
    .filter((c) => c.id !== id)
    .sort((a, b) => (b.start_date ?? '').localeCompare(a.start_date ?? ''));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit campaign — ${campaign!.name}` : 'New campaign'}
      maxWidth={600}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {editing ? 'Save changes' : 'Create campaign'}
          </Button>
        </>
      }
    >
      <Field label="Campaign name" required>
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', position: 'relative' }}>
          <button
            type="button"
            onClick={() => setEmojiOpen((o) => !o)}
            title={emoji ? 'Change icon' : 'Add an icon'}
            style={{
              flexShrink: 0,
              width: 44,
              borderRadius: 'var(--r-md)',
              border: `1px solid var(--border)`,
              background: emoji ? 'var(--bg-elevated)' : 'var(--bg-subtle)',
              cursor: 'pointer',
              fontSize: emoji ? 22 : 12,
              color: 'var(--ink-4)',
              lineHeight: 1,
            }}
          >
            {emoji || '＋'}
          </button>
          <input
            className="input"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Summer Sales"
            style={{ flex: 1 }}
          />
          {emojiOpen && (
            <>
              <div
                onClick={() => setEmojiOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div style={{ position: 'absolute', top: 52, left: 0, zIndex: 41 }}>
                <EmojiPicker
                  emojiStyle={EmojiStyle.TWITTER}
                  onEmojiClick={(d: EmojiClickData) => {
                    setEmoji(d.emoji);
                    setEmojiOpen(false);
                  }}
                  width={320}
                  height={380}
                  previewConfig={{ showPreview: false }}
                />
              </div>
            </>
          )}
        </div>
        {emoji && (
          <button
            type="button"
            className="link"
            onClick={() => setEmoji('')}
            style={{ fontSize: 12, color: 'var(--warn)', marginTop: 6 }}
          >
            Remove icon
          </button>
        )}
      </Field>

      <Field label="Type">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CAMPAIGN_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={`filter-pill ${type === t ? 'active' : ''}`}
              onClick={() => setType(t as CampaignType)}
            >
              <span
                className="dot"
                style={{
                  background:
                    t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)',
                }}
              />
              {t}
            </button>
          ))}
        </div>
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field
          label="Start date"
          hint={startDate ? undefined : 'Leave empty to add to the "To plan" group at the top of the campaigns list.'}
        >
          <input
            className="input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <div style={{ marginTop: 6 }}>
            <CampaignDateHeatmapPicker
              value={startDate}
              onPick={(iso) => setStartDate(iso)}
              excludeCampaignId={campaign?.id}
            />
          </div>
        </Field>
        <Field label="End date">
          <input
            className="input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Nested under"
        hint="Optional — link this campaign as a child of an existing one (e.g. a brand-funded push inside a Promotion or Theme)."
      >
        <select className="input" value={parent} onChange={(e) => setParent(e.target.value)}>
          <option value="">(no parent — top-level campaign)</option>
          {parentOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} — {c.type}{c.end_date && c.end_date < todayStr ? ' (ended)' : ''}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Brands" hint={`${selectedBrands.length} selected`}>
        <BrandChipPicker
          brands={brands}
          selected={selectedBrands}
          onToggle={toggleBrand}
          alphabetical
        />
        {(() => {
          const selectedBrandObjs = brands.filter((b) => selectedBrands.includes(b.id));
          const restricted = selectedBrandObjs.filter((b) => b.discount_stance === 'restricted');
          const negotiate = selectedBrandObjs.filter((b) => b.discount_stance === 'negotiate');
          return (
            <>
              {restricted.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    borderRadius: 'var(--r-md)',
                    background: '#fde7e7',
                    color: '#8b1d1d',
                    fontSize: 12,
                    lineHeight: 1.4,
                    border: '1px solid #f3c0c0',
                  }}
                >
                  <strong>⚠ No-discount brand{restricted.length > 1 ? 's' : ''}:</strong>{' '}
                  {restricted.map((b) => b.name).join(', ')}.{' '}
                  Only add to a campaign if you've cleared an exception with the brand.
                </div>
              )}
              {type === 'Promotion' && negotiate.length > 0 && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    borderRadius: 'var(--r-md)',
                    background: '#fff4e0',
                    color: '#9a5400',
                    fontSize: 12,
                    lineHeight: 1.4,
                    border: '1px solid #f3d9a0',
                  }}
                >
                  <strong>Remember to inform:</strong>{' '}
                  {negotiate.map((b) => b.name).join(', ')} — these brands want a heads-up
                  before being included in a promotion.
                </div>
              )}
            </>
          );
        })()}
      </Field>

      <Field label="Description">
        <textarea
          className="input"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this campaign about?"
        />
      </Field>
      <div style={{ marginTop: -8, marginBottom: 14 }}>
        <AiTextSuggest
          current={description}
          onUse={setDescription}
          note="Claude uses the name, type, dates and brands in this form (and the placements, once the campaign exists) — nothing invented."
          fetchOptions={async (brief, current) => (await axios.post('api/campaigns/suggest-description/', {
            name, type, start_date: startDate, end_date: endDate, brands: selectedBrands,
            campaign: campaign?.id, brief, current,
          })).data.options ?? []}
        />
      </div>

      {name && (
        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
          Slug: <span className="mono">{id}</span>
          {editing && ' (cannot be changed)'}
        </div>
      )}
      <BrandRemovalCheck
        open={!!removalItems}
        items={removalItems ?? []}
        busy={removalBusy || pending}
        onConfirm={(also) => doSubmit(also)}
        onClose={() => setRemovalItems(null)}
      />
    </Modal>
  );
};

export default CreateCampaignModal;
